// The live barn rail — who is selling today, biggest receipts first.
//
// Owner, 2026-09-30: "list the barns that are currently trading and have traded
// for the day sorted by receipts and show the receipts live as the come in with
// no minimum head lot … one line per barn with a blink live lot … three dots to
// the right on the row that when you click on it it opens up a tooltip window
// with links to access the barns leader board, live sale page, or the weighted
// average page".
//
// It sits beside the band board in the space freed by taking that grid from
// four columns to three — the owner's own framing: "we are making room on the
// page by reducing the number of columns".
//
// ── WHY THIS IS ALLOWED TO LIST BARNS ───────────────────────────────────────
// ⚠️ THERE IS A STANDING OWNER RULE THAT BARNS ARE NEVER LISTED SIDE BY SIDE
// WITH PRICES (2026-09-11). It is written into src/lobby/lobbyData.ts and
// TickerMap.tsx, and the reason is not cosmetic: a public board showing which
// barn pays under the average is adversarial to the barns, who are the customer
// for the barn-app product.
//
// This rail is compliant because HEAD COUNT IS VOLUME, NOT PRICE — the exact
// distinction TickerMap draws for the dot sizes on the map right above it. The
// /barns directory already ranks barns this way with the same blinking badge.
//
// ⚠️ DO NOT ADD A $/CWT, A WEIGHTED AVERAGE, OR ANY PRICE-BASED SORT TO THESE
// ROWS. That crosses the 2026-09-11 line and is an owner decision, not a tool
// decision. The weighted average is reachable from the row's menu — on that
// barn's OWN page, which is where barn-level price belongs.
//
// ── NO MINIMUM HEAD, AND NOTHING TO REMOVE ──────────────────────────────────
// The owner asked for no head floor. rpc_cattle_ticker_barns has none: it sums
// every animal that crossed the scale, with no head_count predicate at all. The
// 10-head floor in this file's neighbours (fetchCattleTicker, fetchBandCompare)
// only ever applied to PRICED band figures, which this rail does not show.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { barnSlug } from "@/lib/barnSlug";
import { useInApp } from "@/rbapp/inApp";
import { fetchLiveBarnSlugs, type TickerBarn } from "@/lib/rbCattleTicker";

interface RailBarn extends TickerBarn {
  slug: string;
  live: boolean;
  /** Sold today and the ring has gone quiet. See the note below. */
  final: boolean;
}

/**
 * ⚠️ LIVE / FINAL IS NOT A NEW DEFINITION — IT IS THE PLATFORM'S, REUSED.
 * Owner, 2026-10-01: "when a sale goes final for the day can we have a final
 * badge like we have a live badge".
 *
 * public.rpc_barn_sale_report and its two siblings already resolve a barn's day
 * on the same 90-minute window this rail's own rpc_cattle_ticker_live_barns
 * uses: live if the last lot landed within 90 minutes, final otherwise.
 *
 * ⚠️ AND IT MUST NOT ALSO REQUIRE THAT THE BOARD IS SHOWING "TODAY". IT DID,
 * FOR ONE EVENING, AND THE OWNER CAUGHT IT AT 00:45 CENTRAL: every badge on
 * the board switched itself off at midnight. Measured at the time —
 *     chicago_now 2026-10-02 00:45 · board_day 2026-10-01 · is_today FALSE
 * The guard was written to stop sixty FINAL badges appearing on a HISTORICAL
 * board. But rpc_cattle_ticker_day always resolves to the most recent day with
 * sales, so the ticker has no historical mode to protect against — the guard
 * never did the job it was written for and only ever fired overnight, which is
 * precisely when FINAL is most true. A sale that happened yesterday is still
 * final; that is what the word means.
 * ⚠️ A TEST ASSERTED THE BUG AND I HAD WATCHED IT FAIL ON PURPOSE, which is
 * worth remembering: proving a test bites proves it is WIRED, not that it is
 * RIGHT.
 *
 * ⚠️ WHAT DOES REMAIN IS THE WAIT FOR THE LIVE SET. Live slugs are fetched
 * after mount (hydration), so until that first response lands the set is empty
 * and EVERY barn would momentarily qualify as final — including the ones
 * selling. A board that blinks FINAL across a running sale is worse than a
 * badge a second late.
 *
 * The 90 minutes is deliberately generous: a sale that pauses to move pens or
 * run a bred-cow string has not finished, and calling it final puts a number in
 * front of a consignor that reads as complete when it is not.
 */
interface MenuItem {
  label: string;
  /** Absent = not built yet. Rendered as a non-link "Coming soon" row. */
  href?: string;
  /**
   * Show the sale's state on this row — PENDING while the ring is running,
   * FINAL once it has gone quiet.
   *
   * ⚠️ ONLY THE SALE REPORT CARRIES IT, and that is the point. Owner,
   * 2026-10-01: "If the sale live can the sale report have a badge that says
   * like pending or live? When it's final have a badge that says final?" The
   * Sale Report is the one destination whose NUMBERS ARE STILL MOVING during a
   * sale — open it at 2pm and the averages are half a day's cattle. PENDING
   * says come back. Putting the same badge on every row would make it
   * wallpaper and tell a reader nothing about which page is safe to quote.
   */
  live?: boolean;
}

function sectionsFor(slug: string): { title: string; items: MenuItem[] }[] {
  return [
    {
      // ⚠️ SALE DATA IS FIRST, AND THE REASON IS THE PHONE (owner, 2026-10-01,
      // with a screenshot of the bottom sheet). BARN led, and all three of its
      // rows are "Coming soon" — so the sheet opened on three dead rows and
      // every working link sat below the fold. Sections are ordered by what a
      // reader can actually DO, not by hierarchy.
      // ⚠️ KEEP THE SECTION WITH REAL LINKS FIRST. If BARN ever gets its routes
      // built, this order is worth revisiting — until then, do not put it back.
      title: "Sale Data",
      items: [
        // "Live Ticker", not "Live Board" (owner, 2026-10-01). The product is
        // called the Cattle Ticker everywhere else; "Board" was this menu's
        // own word for it and nothing else on the site used it.
        { label: "Live Ticker", href: `/barn/${slug}/ticker` },
        { label: "Leaderboard", href: `/barn/${slug}/leaderboard` },
        { label: "Sale Report", href: `/barn/${slug}/live`, live: true },
        { label: "Weighted Averages", href: `/sheet/${slug}` },
        { label: "Records" },
        { label: "Receipts", href: "/receipts" },
      ],
    },
    {
      title: "Barn",
      // ⚠️ NO "Landing Page" ROW (owner, 2026-10-01: "remove landing page").
      // /barn/:slug still exists and is still linked from the map, the barns
      // directory and search — it is just not offered twice from a menu that
      // opens on that barn's own row.
      // ⚠️ NO "Weather" AND NO "Watch Live" ROW (owner, 2026-10-01: "Let's just
      // remove weather for now from the menu", then "Remove watch live as
      // well"). Taken out one at a time, on two separate instructions — the
      // remaining five "Coming soon" rows are deliberate and stay.
      //
      // Watch Live had the furthest to go of any of them. It needs a per-barn
      // broadcast URL, and there is no such thing to store: DVAuction has no
      // persistent per-seller page, so every link is a dated per-sale event
      // that expires weekly, and 25 of the 60 barns live on 2026-10-01 do not
      // broadcast with DVAuction at all. See the Watch Live row in
      // NEXT_SESSION.md before reviving this.
      // The groundwork exists on branch claude/barn-weather-groundwork and is
      // NOT applied: the table was never created, because the approval prompt
      // for a database write cannot be answered from a phone. Re-add this row
      // when a barn weather page actually renders.
      items: [
        { label: "Consignments" },
      ],
    },
    {
      title: "Market Tools",
      // ⚠️ COMING SOON, NOT LINKS (owner, 2026-10-01). /steer-tracker and
      // /calf-tracker are both built and working, but both are gated, and the
      // owner took the gate wording off this menu — so a public barn menu is
      // no longer the place to send someone into a sign-in wall from. Revisit
      // when there is a barn-scoped version of either.
      items: [
        { label: "Steer Tracker" },
        { label: "Calf Tracker" },
        { label: "Charts" },
      ],
    },
  ];
}

/**
 * "Oct 1" from "2026-10-01".
 *
 * ⚠️ PARSED, NOT Date-FORMATTED. This rail ships inside the prerendered
 * homepage, where the HTML is built in Node and then hydrated in a browser. A
 * `new Date(iso).toLocaleDateString()` can disagree between the two on timezone
 * alone — the server is UTC, the reader is not — and a first render that
 * differs from the served HTML fails hydration for the WHOLE page. Reading the
 * three fields off the string cannot drift.
 */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function shortDate(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? "");
  if (!m) return "";
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${month} ${Number(m[3])}` : "";
}

/**
 * Where the panel sits, in viewport coordinates.
 *
 * `null` on a phone, where the sheet is pinned to the bottom edge by the 760px
 * media query in tickerHome.css and an inline coordinate would beat it.
 */
type PopPos = { left: number; top: number } | null;

/** The same breakpoint as the bottom-sheet media query. Keep them together. */
const SHEET_MAX_PX = 760;

/**
 * How far down the sheet must be dragged before releasing it closes it.
 *
 * ⚠️ FAR ENOUGH TO BE A DECISION. A short threshold turns a mis-touch while
 * reading the barn name into a dismissal, and the sheet takes two taps to get
 * back. Anything short of this springs back.
 */
const SHEET_DISMISS_PX = 90;

/**
 * Where the panel is portaled to.
 *
 * ⚠️ `.rbtx`, NOT document.body — AND THIS SHIPPED BROKEN ONCE. Every colour on
 * this board is a custom property declared on `.rbtx` (--tx-navy, --tx-cream,
 * --tx-gold, the lot). Custom properties INHERIT, so a panel portaled to
 * document.body is outside that scope and every var() in its stylesheet
 * resolves to nothing: the background fell away entirely and the menu rendered
 * as bare text floating over the board. The unit tests did not catch it because
 * jsdom does not compute custom properties — only the live page shows it.
 *
 * `.rbtx` is still far enough out to fix the original bug: the panel has to
 * escape the barn ROW and the RAIL, and `.rbtx` is the page root above both.
 *
 * ⚠️ `.rbtx` MUST NOT BECOME A STACKING CONTEXT. It works as a portal target
 * because it is unpositioned with no transform, filter, isolation or
 * will-change, so the panel's `position: fixed; z-index: 70` still resolves
 * against the viewport and the root stacking context. Add any of those to
 * `.rbtx` and the headers paint over the menu again. liveBarnRail.test.tsx
 * guards this.
 */
function popHost(from: HTMLElement | null): HTMLElement {
  return from?.closest<HTMLElement>(".rbtx") ?? document.body;
}

function RowMenu({ barn, dayShort }: { barn: RailBarn; dayShort: string }) {
  // Inside the App Store app the menu shows only what opens: a "Coming soon" row
  // is placeholder content to App Review (2.1), 2026-10-31. The website keeps
  // them (owner, 2026-10-01: "put coming soon and don't make them links").
  const inApp = useInApp();

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<PopPos>(null);
  /** How far the phone sheet has been dragged down, in px. 0 = at rest. */
  const [dragY, setDragY] = useState(0);
  const dragFrom = useRef<number | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  /**
   * Place the panel in viewport coordinates, measured from its trigger.
   *
   * ⚠️ THIS IS THE FIX FOR "It's going behind the headers" (owner, 2026-10-01,
   * with a screenshot of the live board). The panel used to be
   * `position: absolute` inside the row, which left it a descendant of the
   * page — painted inside its ancestors' stacking context and clipped to their
   * boxes, so the statement band could cover it. It is now portaled to
   * document.body and positioned here. No ancestor is left to lose to, and no
   * z-index arms race is needed: the grep for a bigger z-index came back empty
   * precisely because the number was never the problem.
   *
   * ⚠️ CLAMPED, NOT FLIPPED. The old `data-up` flip anchored the panel's bottom
   * to the row's bottom when there was no room below, which fixed one edge and
   * broke the other — flipped up from a row near the top of the screen it ran
   * off the top, which is the clipping the owner photographed. A clamp holds it
   * fully on screen in both directions, at all 71 rows.
   *
   * ⚠️ useLayoutEffect, AND ONLY WHILE OPEN. It runs after the panel is in the
   * DOM but before paint, so the move never shows as a jump. It reads layout,
   * which must never happen during render on this component — the rail ships
   * inside the prerendered homepage and a first render that differs from the
   * served HTML fails hydration for the whole page.
   */
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const el = panel.current;
    const trigger = box.current;
    if (!el || !trigger) return;

    // Phone: CSS owns the sheet. See SHEET_MAX_PX.
    const sheet =
      typeof window.matchMedia === "function" &&
      window.matchMedia(`(max-width: ${SHEET_MAX_PX}px)`).matches;
    if (sheet) { setPos(null); return; }

    const place = () => {
      const t = trigger.getBoundingClientRect();
      const p = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const M = 12; // the gutter the panel never crosses

      // Opens into the board on the right — the rail is 300px and the reader's
      // eye is already there. Falls back to the trigger's left when the board
      // side has no room, which is what happens once the rail goes full width.
      let left = t.right + 10;
      if (left + p.width > vw - M) left = t.left - 10 - p.width;
      left = Math.max(M, Math.min(left, vw - M - p.width));

      // Lines the panel's first row up with the row that opened it, then holds
      // it inside the screen.
      let top = t.top - 6;
      top = Math.max(M, Math.min(top, vh - M - p.height));

      setPos({ left, top });
    };

    place();
    // A fixed panel does not travel with the row it came from, so it is
    // re-placed rather than left behind. `true` catches the scroll on any
    // ancestor, not just the window.
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  // Close on an outside click or Escape. A menu that can only be closed by
  // opening another one is a menu that covers the board.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      const t = e.target as Node;
      // ⚠️ BOTH REFS. The panel is portaled to document.body, so it is NO
      // LONGER INSIDE `box` — checking only the trigger would close the menu
      // on the very click that was meant to use it, and every link in it would
      // stop working.
      if (box.current?.contains(t)) return;
      if (panel.current?.contains(t)) return;
      setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  /**
   * Swipe the phone sheet down to dismiss it.
   *
   * ⚠️ THE GRAB HANDLE USED TO BE A LIE. It was drawn and the code said so in
   * as many words — "decorative — the sheet is dismissed by the scrim or
   * Escape, not by dragging" — so the one control that LOOKS draggable did
   * nothing. The owner found it the first time he opened the sheet on a phone:
   * "This won't slide down on the phone." Same class of mistake as the
   * readOnly share-card inputs: a control that advertises a gesture and
   * ignores it is worse than no control.
   *
   * ⚠️ THE DRAG REGION IS THE HEADER, NOT THE WHOLE SHEET. The body is a
   * scroller (max-height + overflow-y on .rbtx-bm-body); a drag handler over
   * it would fight the thumb on every scroll. Dragging the head is the gesture
   * people already know, and it is the part that cannot scroll.
   */
  const onDragStart = (e: React.TouchEvent) => {
    dragFrom.current = e.touches[0]?.clientY ?? null;
  };
  const onDragMove = (e: React.TouchEvent) => {
    if (dragFrom.current === null) return;
    const dy = (e.touches[0]?.clientY ?? 0) - dragFrom.current;
    // Down only. Dragging a bottom sheet UP would lift it off its own edge.
    setDragY(dy > 0 ? dy : 0);
  };
  const onDragEnd = () => {
    if (dragFrom.current !== null && dragY > SHEET_DISMISS_PX) setOpen(false);
    dragFrom.current = null;
    setDragY(0);
  };

  return (
    <div className="rbtx-lbr-menu" ref={box}>
      <button
        type="button"
        className="rbtx-lbr-dots"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Open ${barn.name}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden="true">⋯</span>
      </button>

      {/* ⚠️ PORTALED OUT OF THE ROW — to `.rbtx`, never document.body. That is
          what stops the panel rendering behind the page's headers, and the host
          choice is what keeps it painted at all: see popHost above. Rendered
          only while open, so the prerendered homepage never contains it and 71
          rows never build 71 panels. */}
      {open && createPortal(
        <>
          {/* Phone only — see tickerHome.css. A bottom sheet with nothing behind
              it reads as a page, not a sheet. */}
          <div className="rbtx-bm-scrim" aria-hidden="true" onClick={() => setOpen(false)} />

          <div
            className="rbtx-lbr-pop"
            ref={panel}
            // Written here and not in CSS because it is measured, not declared.
            // Absent on a phone, where the media query pins the sheet instead.
            style={
              pos
                ? { left: pos.left, top: pos.top }
                : dragY
                  ? // Follows the thumb 1:1. `transition: none` so it tracks the
                    // finger; dropping both on release lets the CSS transition
                    // spring it back.
                    { transform: `translateY(${dragY}px)`, transition: "none" }
                  : undefined
            }
            role="dialog"
            aria-label={`${barn.name} menu`}
          >
            {/* ⚠️ THE DRAG REGION. Grip and header together, so the whole top
                of the sheet answers the gesture rather than a 38x4px bar.
                Touch-only by design: the scrim and Escape remain the pointer
                and keyboard ways out, and neither was removed. */}
            <div
              className="rbtx-bm-drag"
              onTouchStart={onDragStart}
              onTouchMove={onDragMove}
              onTouchEnd={onDragEnd}
              onTouchCancel={onDragEnd}
            >
            <div className="rbtx-bm-grip" aria-hidden="true" />

            <header className="rbtx-bm-head">
              <p className="rbtx-bm-name">{barn.name}</p>
              <p className="rbtx-bm-status">
                {/* ⚠️ GREEN ONLY WHEN THE BARN IS ACTUALLY SELLING. The live set
                    comes from rpc_cattle_ticker_live_barns via
                    fetchLiveBarnSlugs — the same source the row badge uses, so
                    the sheet can never disagree with the row that opened it. */}
                {/* ⚠️ THREE STATES, NOT TWO. This said LIVE/OFFLINE while the
                    rail row behind it said FINAL — two words for one state on
                    one screen, which the owner spotted in a screenshot. FINAL
                    is the cattleman's word and it is a positive statement: the
                    head count is complete. OFFLINE now means only what it says
                    — no sale here today. */}
                <span className={barn.live ? "rbtx-bm-live" : "rbtx-bm-off"}>
                  <i aria-hidden="true" />
                  {barn.live ? "LIVE" : barn.final ? "FINAL" : "OFFLINE"}
                </span>
                {/* ⚠️ THE REAL COUNT, EVEN WHEN OFFLINE. A barn that finished at
                    noon still sold its cattle; printing 0 head after the ring
                    closes would be inventing a number.
                    ⚠️ AND THE DATE SITS WITH IT (owner, 2026-10-01: "put the
                    date next to the head count so they know the date"). The
                    rail carries no date of its own, so a reader on a Monday
                    morning had no way to tell whether this was today's run or
                    Friday's. */}
                <span className="rbtx-bm-headn">
                  {barn.head.toLocaleString("en-US")} head
                  {dayShort ? ` · ${dayShort}` : ""}
                </span>
              </p>
              {/*
                ⚠️ THIS LINE IS AN ANSWER, NOT DECORATION. Owner asked of the
                head count: "does it include all head?" Traced to source and
                verified on prod: rpc_cattle_ticker_barns is
                sum(head_count) over dv_sale_market_reports for the day, with
                NO class filter, NO weight filter and NO head floor. On
                2026-10-01 Stockmen's EAST showed 2,779 — 1,080 steers, 1,547
                heifers, 96 cows, 56 bulls, 267 head at 1,000 lb and up, and
                146 of its 311 lots were single head.

                So it is BROADER than the board beside it, which shows 300–999
                lb and one class at a time: the same barn's board-relevant head
                was 2,512, and its steers alone 1,080. Without this line the two
                numbers look like a bug. It is also a RUNNING total — it climbs
                as the day's lots print.
              */}
              <p className="rbtx-bm-scope">All classes and weights</p>
            </header>
            </div>

            <div className="rbtx-bm-body">
              {sectionsFor(barn.slug).map((sec) => ({ ...sec, items: inApp ? sec.items.filter((it) => it.href) : sec.items })).filter((sec) => sec.items.length > 0).map((sec) => (
                <section className="rbtx-bm-sect" key={sec.title}>
                  <h4 className="rbtx-bm-secth">{sec.title}</h4>
                  <ul className="rbtx-bm-list">
                    {sec.items.map((it) =>
                      it.href ? (
                        <li key={it.label}>
                          {/* ⚠️ NO CHEVRON. Owner, 2026-10-01: "it looks busy."
                              Eight chevrons were the eighth kind of mark in a
                              panel that already had column rules, three
                              headings and thirteen rows. The rules went too;
                              the hover fill is what says "link" now, and the
                              owner's own spec for this menu said no icons. */}
                          <a className="rbtx-bm-row rbtx-lbr-poplink" href={it.href}>
                            <span className="rbtx-bm-label">{it.label}</span>
                            {it.live && barn.live && (
                              <span
                                className="rbtx-bm-rowtag"
                                data-state="pending"
                                aria-label="Sale still running — these figures are not final"
                              >
                                PENDING
                              </span>
                            )}
                            {it.live && barn.final && (
                              <span className="rbtx-bm-rowtag" data-state="final">
                                FINAL
                              </span>
                            )}
                          </a>
                        </li>
                      ) : (
                        <li key={it.label}>
                          {/* ⚠️ NOT A LINK, AND NOT FOCUSABLE. See the comment on
                              sectionsFor: these pages do not exist. */}
                          <span className="rbtx-bm-row rbtx-bm-soon" aria-disabled="true">
                            <span className="rbtx-bm-label">{it.label}</span>
                            <span className="rbtx-bm-soontag">Coming soon</span>
                          </span>
                        </li>
                      ),
                    )}
                  </ul>
                </section>
              ))}
            </div>
          </div>
        </>,
        popHost(box.current),
      )}
    </div>
  );
}

export default function LiveBarnRail({ barns, day }: { barns: TickerBarn[]; day?: string | null }) {
  const dayShort = shortDate(day);
  const [live, setLive] = useState<Set<string>>(() => new Set());
  /** Has the live set actually come back? Until it has, nothing is final. */
  const [liveKnown, setLiveKnown] = useState(false);

  // ⚠️ AFTER MOUNT, NEVER DURING RENDER. This rail ships inside the prerendered
  // homepage (scripts/prerender-rbhome.mjs), and a first render that differs
  // from the served HTML fails hydration for the WHOLE page — the rule stated
  // at the top of TickerBoard. So the first paint has no live dots and they
  // arrive on the first tick, exactly like the board's own numbers.
  useEffect(() => {
    let alive = true;
    const load = () => {
      void fetchLiveBarnSlugs()
        .then((s) => { if (alive) { setLive(s); setLiveKnown(true); } })
        .catch(() => { /* a rail with no dots still lists the barns */ });
    };
    load();
    // Matches the board's 30s poll and the 30s edge cache on
    // rpc_cattle_ticker_live_barns — asking faster re-reads the same bytes.
    const t = setInterval(load, 30_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const rows: RailBarn[] = barns.map((b) => {
    const slug = barnSlug(b.name, b.state);
    const isLive = live.has(slug);
    return {
      ...b,
      slug,
      live: isLive,
      // ⚠️ TWO CONDITIONS. There were three; see the note on RailBarn.final for
      // why the third was wrong.
      final: liveKnown && !isLive,
    };
  });

  const liveCount = rows.filter((r) => r.live).length;

  if (!rows.length) {
    return (
      <aside className="rbtx-lbr" aria-label="Barns selling today">
        <div className="rbtx-lbr-head">
          <h3 className="rbtx-lbr-title">Barns today</h3>
        </div>
        <p className="rbtx-lbr-empty">No sales reported yet.</p>
      </aside>
    );
  }

  return (
    <aside className="rbtx-lbr" aria-label="Barns selling today">
      <div className="rbtx-lbr-head">
        <h3 className="rbtx-lbr-title">Barns today</h3>
        {/* Counts, not prices. Says the sort out loud so nobody has to infer
            why Joplin is above Faith. */}
        <span className="rbtx-lbr-count">
          {liveCount > 0 && (
            <>
              <span className="rbtx-lbr-now">{liveCount} selling now</span>
              {" · "}
            </>
          )}
          {rows.length} {rows.length === 1 ? "barn" : "barns"} · most head first
        </span>
      </div>

      {/* ⚠️ EVERY BARN, NO INNER SCROLLER. This shipped capped at 12 rows with
          the rest behind a scroll inside the rail; owner, 2026-09-30: "No I
          want all the barns not just twelve rows."
          A nested scroller was the wrong call anyway — a small scrolling box
          inside a page that also scrolls is awkward with a mouse and worse
          with a thumb, and it hides the very thing the rail is for. The page
          simply gets as tall as the day's list, which on 2026-09-30 was 71
          barns. */}
      <ol className="rbtx-lbr-list">
        {rows.map((b) => (
          <li className="rbtx-lbr-row" key={b.slug} data-live={b.live ? "1" : undefined}>
            {/* ⚠️ TWO LINES: THE NAME OWNS THE FIRST ONE OUTRIGHT.
                Owner, 2026-10-01: "What if we put the final and head count in a
                second line and made the rows shorter". Measured on the 60 barns
                live that day — sharing the line with the receipts and the dots
                left the name ~160px of a 300px rail and wrapped 45 of the 60
                names to two or three lines. Given the full width it is 2.
                ⚠️ IT DOES NOT MAKE THE LIST SHORTER ON ITS OWN, and that was
                measured before building: the second line costs exactly what the
                un-wrapping saves (2,796px either way). What it buys is a name
                that reads, and a row shaped to survive the two-column rail
                below — where each column is ~310px and the name again owns it. */}
            {/* ⚠️ A PLAIN <a>, NEVER <Link> — see the note at the top of this
                file. This rail ships inside the PRERENDERED homepage, which
                mounts no Router; a <Link> there throws on render and takes the
                whole document with it.
                Owner, 2026-10-02: "make the barn list in the home page on the
                left sidebar clickable so when they click on the barn name it
                goes to that barn's ticker". Same destination as the row menu's
                "Live Ticker" — the menu keeps it because it also carries the
                other four, and a reader should not have to open a menu to do
                the obvious thing. */}
            <a
              className="rbtx-lbr-name"
              href={`/barn/${b.slug}/ticker`}
              title={`${b.name}, ${b.state} — open this barn's ticker`}
            >
              {/* ⚠️ THE NAME IS ITS OWN ELEMENT AND THE BADGE IS ITS SIBLING —
                  now on a different LINE, which is stronger still. They were one
                  box with overflow:hidden, so a long name pushed the LIVE badge
                  past the clip and it vanished: the header said "9 selling now"
                  with not one badge visible on a phone. */}
              <span className="rbtx-lbr-nametext">{b.name}</span>
            </a>

            {/* ⚠️ RECEIPTS FIRST, BADGE AFTER. The head count is what this rail
                is sorted by, so putting it at the start of the line puts every
                figure on the same left edge and the eye runs straight down the
                ranking. A badge first would push each number to a different x. */}
            <span className="rbtx-lbr-meta">
              <span className="rbtx-lbr-head-n">{b.headLabel}</span>
              {b.live && (
                <span className="rbtx-dir-live" aria-label="Live now — selling">
                  <i aria-hidden="true" />
                  LIVE
                </span>
              )}
              {/* ⚠️ NO PULSING DOT. The dot is what says "happening now"; a
                  finished sale that blinks is the opposite of the message. */}
              {b.final && (
                <span className="rbtx-dir-final" aria-label="Sale final for the day">
                  FINAL
                </span>
              )}
            </span>
            <RowMenu barn={b} dayShort={dayShort} />
          </li>
        ))}
      </ol>

      <p className="rbtx-lbr-foot">
        Head through the ring today, every lot — no minimum.
      </p>
    </aside>
  );
}
